import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  DocumentArrowDownIcon,
  PlusIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { KpiCard } from '../components/dashboard/KpiCard';
import { RecentActivityList } from '../components/dashboard/RecentActivityList';
import { EmptyState } from '../components/common/EmptyState';
import { fetchInspections, fetchProperties, fetchSubscription, fetchReports } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import { formatDistanceToNow } from '../lib/time';

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: inspections = [], isLoading: inspectionsLoading } = useQuery({
    queryKey: ['inspections', user?.organizationId],
    queryFn: () => fetchInspections({}),
    enabled: Boolean(user)
  });

  const { data: properties = [], isLoading: propertiesLoading } = useQuery({
    queryKey: ['properties', user?.organizationId],
    queryFn: () => fetchProperties(),
    enabled: Boolean(user)
  });

  const { data: reportsData } = useQuery({
    queryKey: ['reports', user?.organizationId],
    queryFn: () => fetchReports({ limit: 10 }),
    enabled: Boolean(user)
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription', user?.organizationId],
    queryFn: async () => {
      if (!user) return undefined;
      try {
        return await fetchSubscription(user.organizationId);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return undefined;
        }
        throw error;
      }
    },
    enabled: Boolean(user),
    staleTime: 1000 * 60 * 5
  });

  const metrics = useMemo(() => {
    const completed = inspections.filter((inspection) => inspection.status === 'completed').length;
    const inProgress = inspections.filter((inspection) => inspection.status === 'in_progress').length;
    const drafts = inspections.filter((inspection) => inspection.status === 'draft').length;
    const reportsCount = reportsData?.meta?.total || 0;
    return {
      completed,
      inProgress,
      drafts,
      reportsCount,
    };
  }, [inspections, reportsData]);

  const recentActivity = useMemo(() => {
    return inspections.slice(0, 5).map((inspection) => {
      const status: 'completed' | 'in_progress' | 'pending' =
        inspection.status === 'draft'
          ? 'pending'
          : inspection.status === 'in_progress'
          ? 'in_progress'
          : 'completed';

      return {
        id: inspection._id,
        title:
          typeof inspection.propertyId === 'string'
            ? `Inspection ${inspection._id.slice(-6)}`
            : inspection.propertyId.name,
        detail: `${inspection.rooms.length} rooms captured · ${inspection.status.replace('_', ' ')}`,
        time: formatDistanceToNow(inspection.updatedAt),
        status
      };
    });
  }, [inspections]);

  const latestUsage = subscription && subscription.usage.length > 0 ? subscription.usage[subscription.usage.length - 1] : undefined;
  const limitUsage = subscription ? `${latestUsage?.totalReports ?? 0} / ${subscription.reportLimit} reports` : 'Usage data unavailable';

  return (
    <div className="space-y-6">
      {/* Quick Actions Bar */}
      <div className="glass-card flex flex-wrap items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Quick actions:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/app/properties')}
            className="btn-glass btn-secondary focus-ring flex items-center gap-1.5 text-xs font-medium"
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            New property
          </button>
          <button
            type="button"
            onClick={() => navigate('/app/items?action=new-inspection')}
            className="btn-glass btn-primary focus-ring flex items-center gap-1.5 text-xs font-medium"
          >
            <PlusIcon className="h-4 w-4" />
            New inspection
          </button>
          <button
            type="button"
            onClick={() => navigate('/app/reports')}
            className="btn-glass btn-secondary focus-ring flex items-center gap-1.5 text-xs font-medium"
          >
            <DocumentTextIcon className="h-4 w-4" />
            View reports
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <KpiCard
          title="Completed"
          value={inspectionsLoading ? '...' : metrics.completed.toString()}
          delta={metrics.completed > 0 ? `${Math.round((metrics.completed / (inspections.length || 1)) * 100)}%` : '—'}
          icon={<ClipboardDocumentCheckIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="In progress"
          value={inspectionsLoading ? '...' : metrics.inProgress.toString()}
          delta={metrics.drafts > 0 ? `${metrics.drafts} drafts` : '—'}
          icon={<ChartBarIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Properties"
          value={propertiesLoading ? '...' : properties.length.toString()}
          delta=""
          icon={<BuildingOffice2Icon className="h-5 w-5" />}
        />
        <KpiCard
          title="Reports"
          value={metrics.reportsCount.toString()}
          delta=""
          icon={<SparklesIcon className="h-5 w-5" />}
        />
      </div>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Portfolio Overview */}
          <div className="glass-card flex flex-col gap-4 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Portfolio overview</h2>
                <p className="text-sm text-muted-foreground">{limitUsage}</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/app/reports')}
                className="btn-glass btn-secondary focus-ring flex items-center gap-1.5 text-xs font-medium"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Export reports
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => navigate('/app/properties')}
                className="group rounded-2xl border border-white/20 bg-white/30 p-4 text-left transition-all hover:bg-white/50 hover:shadow-md dark:bg-white/10 dark:hover:bg-white/15"
              >
                <p className="text-xs text-muted-foreground">Properties</p>
                <p className="mt-2 text-2xl font-semibold text-foreground group-hover:text-primary transition-colors">
                  {propertiesLoading ? '—' : properties.length}
                </p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/app/items')}
                className="group rounded-2xl border border-white/20 bg-white/30 p-4 text-left transition-all hover:bg-white/50 hover:shadow-md dark:bg-white/10 dark:hover:bg-white/15"
              >
                <p className="text-xs text-muted-foreground">Active inspections</p>
                <p className="mt-2 text-2xl font-semibold text-amber-600 group-hover:text-primary transition-colors">
                  {metrics.inProgress + metrics.drafts}
                </p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/app/billing')}
                className="group rounded-2xl border border-white/20 bg-white/30 p-4 text-left transition-all hover:bg-white/50 hover:shadow-md dark:bg-white/10 dark:hover:bg-white/15"
              >
                <p className="text-xs text-muted-foreground">Avg. condition</p>
                <p className="mt-2 text-2xl font-semibold text-primary transition-colors">
                  4.3 / 5
                </p>
              </button>
            </div>
          </div>

          {/* Today's Focus */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Recent inspections</h3>
                <p className="text-sm text-muted-foreground">Continue where you left off.</p>
              </div>
              <button
                className="btn-glass btn-secondary focus-ring flex items-center gap-1.5 text-xs font-medium"
                onClick={() => navigate('/app/items')}
                type="button"
              >
                <ArrowTrendingUpIcon className="h-4 w-4" />
                View all
              </button>
            </div>
            {inspectionsLoading ? (
              <div className="mt-6 space-y-4">
                {[...Array(3).keys()].map((key) => (
                  <div key={key} className="h-16 animate-pulse rounded-xl bg-white/40 dark:bg-white/10" />
                ))}
              </div>
            ) : inspections.length > 0 ? (
              <ul className="mt-6 space-y-3">
                {inspections.slice(0, 4).map((inspection) => (
                  <li
                    key={inspection._id}
                    className="group flex items-center justify-between rounded-xl border border-white/10 bg-white/30 px-4 py-3 transition-all hover:bg-white/50 hover:shadow-sm dark:bg-white/10 dark:hover:bg-white/15"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-2.5 w-2.5 rounded-full ${
                        inspection.status === 'completed' ? 'bg-emerald-500' :
                        inspection.status === 'in_progress' ? 'bg-amber-500' : 'bg-gray-400'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {typeof inspection.propertyId === 'string' ? inspection.propertyId : inspection.propertyId.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {inspection.rooms.length} rooms · {inspection.status.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn-glass btn-primary focus-ring text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                      type="button"
                      onClick={() => navigate(`/app/items/${inspection._id}`)}
                    >
                      Continue
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="No inspections yet"
                description="Create your first inspection to start using InspectAI's intelligent workflows."
                actionLabel="Start new inspection"
                onAction={() => navigate('/app/items?action=new-inspection')}
              />
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-foreground">Activity feed</h3>
            <p className="text-sm text-muted-foreground">AI summaries and exports.</p>
            <div className="mt-4">
              {recentActivity.length > 0 ? (
                <RecentActivityList items={recentActivity} />
              ) : (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No recent activity
                </div>
              )}
            </div>
          </div>

          {/* AI Insights Card */}
          <div className="glass-card space-y-4 p-6">
            <h3 className="text-lg font-semibold text-foreground">AI insights</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-900/20">
                <SparklesIcon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  AI has analyzed <strong>{metrics.completed}</strong> inspections this month with 
                  <strong> 94%</strong> accuracy on issue detection.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                • Predictive analysis learns your inspection patterns<br />
                • Multi-language reports coming soon<br />
                • Voice dictation in beta
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/app/report-presets')}
              className="btn-glass btn-primary focus-ring w-full text-xs font-medium"
            >
              Manage templates
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
